
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getShoppingList } from "@/services/shopping/lists.service";
import { getPrimaryList } from "@/services/shopping/lists/primary-list.service";
import { NavigateFunction } from "react-router-dom";
import { getUserCategories } from "@/services/categories.service";
import { ShoppingList } from "@/types";
import { useAuth } from "@/context/auth/AuthContext";
import { getShoppingLists } from "@/services/shopping/lists/get-lists.service";

interface UseShoppingPageDataProps {
  listId: string | null;
  navigate: NavigateFunction;
}

export function useShoppingPageData({ listId, navigate }: UseShoppingPageDataProps) {
  const { user } = useAuth();
  const [hasMultipleLists, setHasMultipleLists] = useState(false);

  // Preload categories for faster rendering
  useQuery({
    queryKey: ['user-categories'],
    queryFn: getUserCategories,
    enabled: !!user,
  });

  const { 
    data: allLists,
    isLoading: allListsLoading
  } = useQuery({
    queryKey: ['shopping-lists'],
    queryFn: getShoppingLists,
    enabled: !!user,
  });

  useEffect(() => {
    if (allLists && Array.isArray(allLists) && allLists.length > 1) {
      setHasMultipleLists(true);
    } else {
      setHasMultipleLists(false);
    }
  }, [allLists]);

  const { 
    data: specificList, 
    isLoading: specificListLoading,
    error: specificListError,
    status: specificListStatus
  } = useQuery({
    queryKey: ['shopping-list', listId],
    queryFn: () => getShoppingList(listId!),
    enabled: !!listId && !!user,
  });


  const {
    data: primaryList,
    isLoading: primaryListLoading
  } = useQuery({
    queryKey: ['primary-list'],
    queryFn: getPrimaryList,
    enabled: !listId && !!user,
  });

  const list = listId ? specificList : primaryList;
  const isLoading = listId ? specificListLoading : primaryListLoading;

  const [hasRedirected, setHasRedirected] = useState(false);

  useEffect(() => {
    if (!hasRedirected && !listId && primaryList && !primaryListLoading && (primaryList as ShoppingList)?.id) {
      setHasRedirected(true); // evita redirect multipli
      navigate(`/shopping?list=${(primaryList as ShoppingList).id}`, { replace: true });
    }
  }, [listId, primaryList, primaryListLoading, navigate, hasRedirected]);

  return {
    list,
    isLoading,
    hasMultipleLists
  };
}
